import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Shield, Search, Star, Briefcase } from 'lucide-react';
import { useUpdateBio } from '@/hooks/useProfile';
import { occupationApi } from '@/services/skillKnowledgeApi';
import type { Occupation } from '@/types/skillKnowledge';
import type { ProfileOccupation, BioUpdateRequest } from '@/types/userProfile';
import { useToast } from '@/hooks/useToast';
import { CopyableId } from '@/components/common';

interface OccupationManagerProps {
  profileId: string;
  occupations: ProfileOccupation[];
  currentOccupation?: ProfileOccupation;
}

export const OccupationManager: React.FC<OccupationManagerProps> = ({
  profileId,
  occupations: initialOccupations,
  currentOccupation,
}) => {
  const [occupations, setOccupations] = useState<ProfileOccupation[]>(initialOccupations);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [masterOccupations, setMasterOccupations] = useState<Occupation[]>([]);
  const [selectedOccupationId, setSelectedOccupationId] = useState('');
  const [occupationName, setOccupationName] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isCurrent, setIsCurrent] = useState(false);
  const updateBio = useUpdateBio();
  const { toast } = useToast();

  // Load master occupations for selection
  useEffect(() => {
    const loadMasterOccupations = async () => {
      try {
        const response = await occupationApi.list({ page: 0, size: 100 });
        setMasterOccupations(response.content || []);
      } catch (error) {
        console.error('Error loading master occupations:', error);
      }
    };
    loadMasterOccupations();
  }, []);

  const filteredMasterOccupations = masterOccupations.filter((occ) =>
    occ.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    occ.id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getOccupationDisplayName = (occ: ProfileOccupation) =>
    occ.name ||
    (masterOccupations.find((o) => o.id === occ.occupationId) as { title?: string } | undefined)?.title ||
    occ.occupationId;

  const handleSave = async () => {
    if (!selectedOccupationId) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Please select an occupation',
      });
      return;
    }

    const newOccupation: ProfileOccupation = {
      occupationId: selectedOccupationId,
      name: occupationName || masterOccupations.find((o) => o.id === selectedOccupationId)?.title,
      verified: isVerified,
    };

    let updatedOccupations: ProfileOccupation[];
    if (editingIndex !== null) {
      updatedOccupations = [...occupations];
      updatedOccupations[editingIndex] = newOccupation;
    } else {
      updatedOccupations = [...occupations, newOccupation];
    }

    setOccupations(updatedOccupations);

    // Update bio
    const bioUpdate: BioUpdateRequest = {
      occupations: updatedOccupations,
      ...(isCurrent && { currentOccupation: newOccupation }),
    };

    try {
      await updateBio.mutateAsync({ userId: profileId, bio: bioUpdate });
      setIsDialogOpen(false);
      resetForm();
    } catch (_error) {
      // Error handled by mutation
    }
  };

  const handleEdit = (index: number) => {
    const occupation = occupations[index];
    setEditingIndex(index);
    setSelectedOccupationId(occupation.occupationId);
    setOccupationName(occupation.name || '');
    setIsVerified(occupation.verified || false);
    setIsCurrent(
      currentOccupation?.occupationId === occupation.occupationId
    );
    setIsDialogOpen(true);
  };

  const handleDelete = async (index: number) => {
    const occupation = occupations[index];
    const isCurrentOcc = currentOccupation?.occupationId === occupation.occupationId;

    const updatedOccupations = occupations.filter((_, i) => i !== index);
    setOccupations(updatedOccupations);

    const bioUpdate: BioUpdateRequest = {
      occupations: updatedOccupations,
      ...(isCurrentOcc && { currentOccupation: undefined }),
    };

    try {
      await updateBio.mutateAsync({ userId: profileId, bio: bioUpdate });
    } catch (_error) {
      // Error handled by mutation
    }
  };

  const handleSetCurrent = async (index: number) => {
    const occupation = occupations[index];
    const bioUpdate: BioUpdateRequest = {
      currentOccupation: occupation,
    };

    try {
      await updateBio.mutateAsync({ userId: profileId, bio: bioUpdate });
    } catch (_error) {
      // Error handled by mutation
    }
  };

  const resetForm = () => {
    setEditingIndex(null);
    setSelectedOccupationId('');
    setOccupationName('');
    setIsVerified(false);
    setIsCurrent(false);
    setSearchQuery('');
  };

  const handleOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) resetForm();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              Occupations
            </CardTitle>
            <CardDescription>Manage user occupations and career history from master data</CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
              <Button onClick={() => resetForm()} className="shrink-0">
                <Plus className="mr-2 h-4 w-4" />
                Add occupation
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingIndex !== null ? 'Edit Occupation' : 'Add Occupation'}
                </DialogTitle>
                <DialogDescription>
                  Select an occupation from master data and set user-specific details
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Search Master Occupations</Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search occupations..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>

                <div>
                  <Label>Select Occupation</Label>
                  <Select value={selectedOccupationId} onValueChange={setSelectedOccupationId}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select an occupation" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredMasterOccupations.map((occ) => (
                        <SelectItem key={occ.id ?? ''} value={occ.id ?? ''}>
                          {occ.title} {occ.industry && `(${occ.industry})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedOccupationId && (
                  <>
                    <div>
                      <Label>Occupation Name (Optional override)</Label>
                      <Input
                        value={occupationName}
                        onChange={(e) => setOccupationName(e.target.value)}
                        placeholder="Leave empty to use master occupation name"
                        className="mt-1"
                        maxLength={100}
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="verified"
                        checked={isVerified}
                        onChange={(e) => setIsVerified(e.target.checked)}
                        className="rounded"
                      />
                      <Label htmlFor="verified" className="cursor-pointer">
                        Verified
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="current"
                        checked={isCurrent}
                        onChange={(e) => setIsCurrent(e.target.checked)}
                        className="rounded"
                      />
                      <Label htmlFor="current" className="cursor-pointer">
                        Set as Current Occupation
                      </Label>
                    </div>
                  </>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => handleOpenChange(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={updateBio.isPending}>
                  {updateBio.isPending ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {occupations.length === 0 ? (
          <div className="rounded-lg border border-dashed bg-muted/20 flex flex-col items-center justify-center py-12 text-center">
            <Briefcase className="h-10 w-10 text-muted-foreground/50 mb-3" />
            <p className="font-medium text-muted-foreground">No occupations yet</p>
            <p className="text-sm text-muted-foreground mt-1">Add occupations from master data to get started</p>
            <Button onClick={() => handleOpenChange(true)} className="mt-4" variant="outline" size="sm">
              <Plus className="mr-2 h-4 w-4" />
              Add occupation
            </Button>
          </div>
        ) : (
          <div className="rounded-lg border bg-muted/20 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Occupation ID</TableHead>
                <TableHead>Verified</TableHead>
                <TableHead>Current</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {occupations.map((occupation, index) => {
                const isCurrentOcc =
                  currentOccupation?.occupationId === occupation.occupationId;
                return (
                  <TableRow key={`${occupation.occupationId}-${index}`}>
                    <TableCell className="font-medium">
                      {getOccupationDisplayName(occupation)}
                    </TableCell>
                    <TableCell>
                      <CopyableId id={occupation.occupationId} label="" showLabel={false} codeClassName="text-xs" />
                    </TableCell>
                    <TableCell>
                      {occupation.verified ? (
                        <Badge
                          variant="outline"
                          className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                        >
                          <Shield className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">Not verified</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isCurrentOcc ? (
                        <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          <Star className="h-3 w-3 mr-1" />
                          Current
                        </Badge>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetCurrent(index)}
                        >
                          Set as Current
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(index)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (
                              window.confirm(
                                'Are you sure you want to remove this occupation?'
                              )
                            ) {
                              handleDelete(index);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};



